import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface LegalPageProps {
  file: 'cgu' | 'tos';
}

export const LegalPage = ({ file }: LegalPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/legal/${file}.md`)
      .then((res) => res.text())
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [file]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
        variant="outlined"
        size="small"
      >
        {t('common.back', 'Retour')}
      </Button>

      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : content ? (
          <Box
            sx={{
              '& h1': { fontSize: '1.8rem', fontWeight: 700, mb: 2, mt: 0 },
              '& h2': { fontSize: '1.3rem', fontWeight: 700, mt: 4, mb: 1.5, borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 },
              '& h3': { fontSize: '1.05rem', fontWeight: 600, mt: 3, mb: 1 },
              '& h4': { fontSize: '0.95rem', fontWeight: 600, mt: 2, mb: 0.5 },
              '& p': { mb: 1.5, lineHeight: 1.7 },
              '& ul, & ol': { pl: 3, mb: 1.5 },
              '& li': { mb: 0.5, lineHeight: 1.7 },
              '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
              '& th': { border: '1px solid', borderColor: 'divider', p: 1, bgcolor: 'action.hover', textAlign: 'left', fontSize: '0.875rem' },
              '& td': { border: '1px solid', borderColor: 'divider', p: 1, fontSize: '0.875rem' },
              '& blockquote': { borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, ml: 0, my: 2, color: 'text.secondary' },
              '& code': { bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: '0.85em', fontFamily: 'monospace' },
              '& strong': { fontWeight: 700 },
              '& a': { color: 'primary.main' },
              '& hr': { border: 'none', borderTop: '1px solid', borderColor: 'divider', my: 3 },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          </Box>
        ) : (
          <Typography color="text.secondary" textAlign="center" py={8}>
            {t('legal.loadError', 'Impossible de charger le document.')}
          </Typography>
        )}
      </Paper>
    </Container>
  );
};
